/**
 * The board view of the graph window (Miro's): every item a plain element placed in board
 * units inside one layer that pans and zooms them all, frames under them, arrows between them,
 * and over the layer the selection's handles, the style bar and the tools. Only what is on
 * screen is drawn, so big boards stay smooth.
 *
 * The board's state lives in its service (`state/board/`): this view holds only what a gesture
 * needs while it lasts (`gestures.ts`), the item whose words are being written, and the
 * clipboard listeners (`paste.ts`). Pictures come from the project's files: dragged from the
 * Assets drawer (`ASSET_DROP`), dropped from the computer, pasted, or picked with the image tool.
 *
 * What a GUI pack adds where it takes part comes through `packs` (`state/packs.ts`): its
 * elements are drawn by `BoardItem`, its frame marks by `Frame`, its board mode's overlay in the
 * layer (UI/UX's prototype mode: where each click leads), its words for an empty board. The
 * stage says the packs taking part (`data-packs`) and the mode in use (`data-mode`).
 *
 * A picture comes in at its own proportions (`pictures.ts`); a
 * double click on one crops it (the whole picture shown faded around the part kept, its handles
 * `data-crop-handle`; Enter or a click elsewhere keeps it, Escape leaves it as it was).
 *
 * A picture of the board (the selection, else everything) is drawn from the same elements: all
 * of them, out of sight at full size (`ExportLayer.tsx`), then made a PNG by `html-to-image` (`picture.ts`).
 *
 * Beside it: `Frame.tsx`, `CropOverlay.tsx`, `Previews.tsx` (what a gesture draws while it lasts),
 * `menu.ts` (the right-click menu), `stylebar/` (the bar above the selection).
 */
import { For, Show, createEffect, createMemo, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { Dynamic } from "solid-js/web";
import { useApp } from "../../../app/context";
import { ASSET_DROP, assetStem, type AssetDrop } from "../../../shared/assets";
import { IMAGE_ELEMENT } from "../../../shared/elements";
import { toast } from "../../../shared/toast";
import { frameAround, parseCrop, type Point } from "../../../state/board";
import { DESIGN } from "../../../state/packs";
import { listenClipboard } from "../paste";
import { Arrows } from "./Arrows";
import { BoardItem } from "./BoardItem";
import { CropOverlay } from "./CropOverlay";
import { ExportLayer } from "./ExportLayer";
import { Frame } from "./Frame";
import { createGestures } from "./gestures";
import { Handles } from "./Handles";
import { boardMenu } from "./menu";
import { exportArea, savePicture, type ExportArea } from "./picture";
import { pictureSize } from "./pictures";
import { DrawPreview, InkPreview, LinkPreview } from "./Previews";
import { StyleBar } from "./stylebar/StyleBar";
import { ToolBar } from "./ToolBar";
import { FRAME_TITLE_PX, MARGIN_PX, PICTURE_GAP } from "./constants";

/** The board. `provideCancel` hands the graph window what Escape calls. */
export function BoardView(props: { provideCancel: (cancel: () => boolean) => void }): JSX.Element {
  const { board, view, actions, access, assets, presence, graph, menu, kinds, packs } = useApp();
  /** What node kind `kind` is on the board. */
  const lookOf = (kind: string) => kinds.board(kind);
  /** What node kind `kind` is drawn as. */
  const elementOf = (kind: string) => lookOf(kind).element;
  let host!: HTMLDivElement;
  let picker!: HTMLInputElement;
  /** The item (or frame) whose words are being written; `<item>::<part>`: a part's own words (an instance's). */
  const [editing, setEditing] = createSignal<string | null>(null);
  /** Where the words being written were clicked (page point; `null`: a double click, every word selected). */
  const [editAt, setEditAt] = createSignal<Point | null>(null);
  /** Starts writing `id`'s words, the caret at page point `at` (none: every word selected). */
  const write = (id: string, at: Point | null = null) => {
    setEditAt(at);
    setEditing(id);
  };
  /** Where the pointer was last (screen pixels of the area), for pastes. */
  let pointer: Point | null = null;
  /** Where the image tool's pictures go once picked. */
  let pictureAt: Point = { x: 0, y: 0 };
  /** The tool the picked pictures are drawn with. */
  let pictureTool = "";
  const canEdit = () => access.can("editor");
  /** What a picture being made holds, drawn out of sight (`null`: none is). */
  const [exporting, setExporting] = createSignal<ExportArea | null>(null);
  let exportHost: HTMLDivElement | undefined;

  /** A mouse event in pixels from the area's corner. */
  const point = (e: MouseEvent): Point => {
    const r = host.getBoundingClientRect();
    return { x: e.clientX - r.left, y: e.clientY - r.top };
  };

  const gestures = createGestures(
    {
      board,
      selectLink: view.selectLink,
      removeLink: (link) => void actions.removeLink(link).then((ok) => ok && toast("Arrow removed. Press Ctrl+Z to undo.")),
      arrowAt: (index) => board.arrows()[index],
      canEdit,
      pickPictures(at, tool) {
        pictureAt = at;
        pictureTool = tool;
        picker.click();
      },
      edit: (id, at) => write(id, at ?? null),
      writable(id) {
        const node = graph.state.graph.nodes.find((n) => n.id === id);
        return !!node && !lookOf(node.kind).def.wordless;
      },
      pictureRatio() {
        const ids = board.selected();
        const node = ids.length === 1 ? graph.state.graph.nodes.find((n) => n.id === ids[0]) : undefined;
        if (!node || elementOf(node.kind) !== "image") return null;
        const img = host.querySelector<HTMLImageElement>(`[data-item="${CSS.escape(node.id)}"] img`);
        const crop = parseCrop(node.style?.crop) ?? { x: 0, y: 0, w: 1, h: 1 };
        if (img?.naturalWidth && img.naturalHeight) return (img.naturalWidth * crop.w) / (img.naturalHeight * crop.h);
        const place = board.content().places.get(node.id);
        return place ? place.w / place.h : null;
      },
      dropModeDrag: async (from, at) => void (await packs.modeDef()?.drop?.(from, at)),
    },
    point,
  );
  props.provideCancel(() => {
    if (board.cropping()) return void board.endCrop(false), true;
    if (editing()) return false;
    return gestures.cancel();
  });

  /** Keeps `files` as project files and puts each on the board as a picture (drawn by `tool`, else the first tool drawing pictures), from `at` along. */
  const placePictures = async (files: File[], at: Point, tool = board.toolFor("image")) => {
    if (!canEdit()) return void toast("Editors and owners add pictures to the board.", "error");
    // Each at its own proportions, read from the file picked before it is sent.
    const sizes = await Promise.all(
      files.map(async (file) => {
        const url = URL.createObjectURL(file);
        try {
          return await pictureSize(url);
        } finally {
          URL.revokeObjectURL(url);
        }
      }),
    );
    const kept = await assets.upload("project", files, "");
    let x = at.x;
    for (const file of kept) {
      const size = sizes[files.findIndex((f) => file.path.endsWith(f.name))] ?? sizes[0] ?? IMAGE_ELEMENT.usual.size;
      await board.create(tool, { x, y: at.y - size.h / 2, ...size }, { name: assetStem(file.path), attachments: [file.path] });
      x += size.w + PICTURE_GAP;
    }
  };

  /** What is on screen (with a margin), by id, in drawing order. */
  const shown = createMemo(() => {
    const c = board.camera();
    const r = host?.getBoundingClientRect();
    const [w, h] = [r?.width || 2000, r?.height || 2000];
    const left = (-MARGIN_PX - c.x) / c.k;
    const top = (-MARGIN_PX - c.y) / c.k;
    const right = (w + MARGIN_PX - c.x) / c.k;
    const bottom = (h + MARGIN_PX - c.y) / c.k;
    const content = board.content();
    // A turned item may reach past its rectangle: count its diagonal.
    const visible = (p: { x: number; y: number; w: number; h: number; rotate?: number }) => {
      const pad = p.rotate ? Math.max(p.w, p.h) / 2 : 0;
      return p.x - pad < right && p.x + p.w + pad > left && p.y - pad < bottom && p.y + p.h + pad > top;
    };
    return { items: content.items.filter((i) => visible(i.place)).map((i) => i.id), frames: content.frames.filter((f) => visible(f.place)).map((f) => f.id) };
  });
  const itemById = createMemo(() => new Map(board.content().items.map((i) => [i.id, i])));
  const frameById = createMemo(() => new Map(board.content().frames.map((f) => [f.id, f])));
  const selected = createMemo(() => new Set(board.selected()));

  onMount(() => {
    const resize = () => {
      const r = host.getBoundingClientRect();
      board.setSize(r.width, r.height);
      if (!board.framed && r.width > 0 && graph.state.loaded) board.fit();
    };
    const observer = new ResizeObserver(resize);
    observer.observe(host);
    resize();
    /** Space held pans with the left button. */
    const key = (e: KeyboardEvent) => {
      if (e.type === "keydown" && e.key === "Enter" && board.cropping()) return void (e.preventDefault(), board.endCrop(true));
      if (e.key === " " && !(document.activeElement instanceof HTMLInputElement) && !(document.activeElement as HTMLElement | null)?.isContentEditable) gestures.setSpace(e.type === "keydown");
    };
    addEventListener("keydown", key);
    addEventListener("keyup", key);
    /** Where pasted things go (the pointer, else the middle of the view). */
    const pasteAt = () => {
      const r = host.getBoundingClientRect();
      const p = pointer ?? { x: r.width / 2, y: r.height / 2 };
      return board.toWorld(p.x, p.y);
    };
    const stopClipboard = listenClipboard({
      active: () => view.mode() === "board" && canEdit(),
      copy: () => board.copy(),
      // A cut frame goes with what it holds.
      remove: () => void board.removeSelection(),
      // Not nodes: the words as a note.
      paste: (text) =>
        void board.paste(text, pasteAt()).then((pasted) => {
          if (!pasted && text.trim()) void board.create(board.toolFor("note"), { ...pasteAt(), w: 0, h: 0 }, { name: text.trim().slice(0, 2000) });
        }),
      pictures: (files) => void placePictures(files, pasteAt()),
    });
    /** A file dragged from the drawer: a picture item where it was dropped (a computer's file copied into the project first). */
    const fromDrawer = async (e: Event) => {
      const drop = (e as CustomEvent<AssetDrop>).detail;
      if (!canEdit()) return void toast("Editors and owners add pictures to the board.", "error");
      const at = point({ clientX: drop.x, clientY: drop.y } as MouseEvent);
      const path = drop.source === "project" ? drop.path : (await assets.copy(drop.source, drop.path, "project", ""))?.path;
      if (!path) return;
      const w = board.toWorld(at.x, at.y);
      const read = await assets.read("project", path, true);
      const url = read ? URL.createObjectURL(new Blob([read.bytes as BlobPart], { type: read.info.mime })) : null;
      const size = url ? await pictureSize(url) : IMAGE_ELEMENT.usual.size;
      if (url) URL.revokeObjectURL(url);
      void board.create(board.toolFor("image"), { x: w.x - size.w / 2, y: w.y - size.h / 2, ...size }, { name: assetStem(path), attachments: [path] });
    };
    host.addEventListener(ASSET_DROP, fromDrawer);
    board.setExporter(async (what) => {
      const area = exportArea(board.content(), what);
      if (!area) return;
      setExporting(area);
      try {
        const name = board.selected().length ? "selection.png" : "board.png";
        if (exportHost) await savePicture(exportHost, area, getComputedStyle(host).backgroundColor, name);
        toast(`Saved ${name}.`);
      } catch (e) {
        toast(`The picture could not be made: ${e instanceof Error ? e.message : String(e)}`, "error");
      } finally {
        setExporting(null);
      }
    });
    onCleanup(() => {
      board.setExporter(null);
      observer.disconnect();
      removeEventListener("keydown", key);
      removeEventListener("keyup", key);
      stopClipboard();
      host.removeEventListener(ASSET_DROP, fromDrawer);
    });
  });
  // The graph arriving after the board was first shown: frame it once.
  createEffect(() => {
    if (graph.state.loaded && !board.framed && board.content().places.size) board.fit();
  });

  /**
   * The element under the pointer. Not the event's target: a press captures the pointer for the
   * board, so the browser sends the clicks that follow to the board itself.
   */
  const under = (e: MouseEvent): Element => document.elementFromPoint(e.clientX, e.clientY) ?? (e.target as Element);

  /** Double click: writes the words of the item or frame under the pointer. */
  const onDoubleClick = (e: MouseEvent) => {
    if (!canEdit()) return;
    const el = under(e);
    const id = el.closest<HTMLElement>("[data-item]")?.dataset.item ?? el.closest<HTMLElement>("[data-frame-title]")?.dataset.frameTitle;
    if (id) {
      const node = graph.state.graph.nodes.find((n) => n.id === id);
      // A picture: cropped (Miro's double click).
      if (node && elementOf(node.kind) === "image") return void board.startCrop(id);
      // An element made of parts (an instance): the own words of the part under the pointer.
      const partAt = node && lookOf(node.kind).def.partAt;
      if (partAt) {
        const part = partAt(el);
        if (part) write(`${id}::${part}`);
        return;
      }
      return void write(id);
    }
    // On the background, a sticky note right there (Miro's double click).
    const p = point(e);
    void board.create(board.toolFor("note"), { ...board.toWorld(p.x, p.y), w: 0, h: 0 }).then((made) => made && write(made));
  };

  /** Right click: selects what is under the pointer (an item, a frame by its title or its background) and offers what can be done with it. */
  const openMenu = (e: MouseEvent) => {
    const el = under(e);
    const p = point(e);
    const at = board.toWorld(p.x, p.y);
    const id = el.closest<HTMLElement>("[data-item]")?.dataset.item ?? el.closest<HTMLElement>("[data-frame-title]")?.dataset.frameTitle ?? frameAround(board.content(), { ...at, w: 0, h: 0 });
    if (id && !board.selected().includes(id)) board.select([id]);
    menu.open(e, [...boardMenu(board, packs, at, canEdit(), write), ...menu.general()]);
  };

  /** Other people's pointers on the board (those drawn in board units). */
  const cursors = () =>
    presence.others().flatMap((person) => {
      const c = person.cursor;
      if (!c || c.anchor || c.in_3d) return [];
      const cam = board.camera();
      return [{ person, x: c.x * cam.k + cam.x, y: c.y * cam.k + cam.y }];
    });

  const cursorOf = () => (board.tool() === "hand" ? "grab" : board.tool() === "select" ? "default" : board.tool() === "eraser" ? "cell" : "crosshair");

  return (
    <div
      ref={host}
      class="board-stage"
      id="board"
      data-board="yes"
      data-asset-target="board"
      data-tool={board.tool()}
      data-mode={packs.mode()}
      data-packs={packs.active().join(" ")}
      data-items={shown().items.length}
      style={{ cursor: cursorOf() }}
      onPointerDown={(e) => {
        // A press elsewhere ends the writing (sending it) and still does what it does.
        if (editing()) (document.activeElement as HTMLElement | null)?.blur();
        host.setPointerCapture?.(e.pointerId);
        gestures.down(e);
      }}
      onPointerMove={(e) => {
        pointer = point(e);
        gestures.move(e);
        const w = board.toWorld(pointer.x, pointer.y);
        presence.moveCursor({ x: w.x, y: w.y, z: 0, in_3d: false, anchor: null, ray: null });
      }}
      onPointerUp={(e) => void gestures.up(e)}
      onPointerCancel={() => gestures.cancel()}
      onPointerLeave={() => presence.moveCursor(null)}
      onWheel={(e) => gestures.wheel(e)}
      onDblClick={onDoubleClick}
      onContextMenu={openMenu}
      onDragOver={(e) => e.dataTransfer?.types.includes("Files") && e.preventDefault()}
      onDrop={(e) => {
        const files = [...(e.dataTransfer?.files ?? [])];
        if (!files.length) return;
        e.preventDefault();
        e.stopPropagation();
        const p = point(e);
        void placePictures(files, board.toWorld(p.x, p.y));
      }}
    >
      <div class="board-layer" style={{ transform: `translate(${board.camera().x}px, ${board.camera().y}px) scale(${board.camera().k})` }}>
        <For each={shown().frames}>
          {(id) => (
            <Show when={frameById().get(id)}>
              {(f) => <Frame frame={f()} selected={selected().has(id)} titlePx={Math.max(FRAME_TITLE_PX, FRAME_TITLE_PX / board.camera().k)} editing={editing() === id} done={() => setEditing(null)} />}
            </Show>
          )}
        </For>
        <Arrows />
        <For each={shown().items}>
          {(id) => (
            <Show when={itemById().get(id)}>
              {(item) => (
                <BoardItem
                  id={id}
                  node={item().node}
                  place={item().place}
                  selected={selected().has(id)}
                  editing={editing() === id}
                  editingPart={editing()?.startsWith(`${id}::`) ? editing()!.slice(id.length + 2) : null}
                  done={() => setEditing(null)}
                  editAt={editAt()}
                  hidden={board.cropping()?.id === id || gestures.erased().has(id)}
                />
              )}
            </Show>
          )}
        </For>
        <Show when={gestures.drawing()}>{(r) => <DrawPreview rect={r()} />}</Show>
        <Show when={gestures.ink()}>{(line) => <InkPreview points={line()} />}</Show>
        <Show when={board.cropping()}>{(c) => <CropOverlay cropping={c()} />}</Show>
        <Show when={packs.modeDef()?.Overlay}>{(Overlay) => <Dynamic component={Overlay()} dragging={gestures.modeDrag()} />}</Show>
        <Show when={gestures.linking()}>{(l) => <LinkPreview from={l().from} to={l().to} />}</Show>
      </div>
      <Handles marquee={gestures.marquee()} hidden={!canEdit() || packs.mode() !== DESIGN || !!board.cropping()} />
      <For each={cursors()}>
        {(c) => (
          <div class="board-cursor" data-board-cursor={c.person.id} style={{ left: `${c.x}px`, top: `${c.y}px`, color: c.person.colour }}>
            <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true">
              <path d="M2 1l11 6-5 1.5L5.5 14z" fill="currentColor" stroke="#fff" stroke-width="1" />
            </svg>
            <span style={{ background: c.person.colour }}>{c.person.name}</span>
          </div>
        )}
      </For>
      <Show when={canEdit() && !board.cropping()}>
        <StyleBar />
      </Show>
      <ToolBar />
      <Show when={exporting()}>{(area) => <ExportLayer area={area()} ref={(el) => (exportHost = el)} />}</Show>
      <input
        ref={picker}
        type="file"
        accept="image/*"
        multiple
        hidden
        id="board-picture-input"
        onChange={(e) => {
          const files = [...(e.currentTarget.files ?? [])];
          e.currentTarget.value = "";
          board.setTool("select");
          if (files.length) void placePictures(files, pictureAt, pictureTool || undefined);
        }}
      />
      <Show when={graph.state.loaded && !board.content().places.size}>
        <div class="empty-graph board-empty">
          <p>The board is empty.</p>
          <p class="muted">{packs.hint() ?? "Pick a tool on the left (S: a sticky note, T: text, R: a shape, Shift+F: a frame), or double-click anywhere."}</p>
        </div>
      </Show>
    </div>
  );
}
